/* Spellbound collection interactions.

   Mounts each section on DOMContentLoaded and shopify:section:load.
   Destroys it on shopify:section:unload (listeners, animation frames,
   observers). shopify:block:select scrolls that lookbook photo into view.

   Roots, with kit class fallbacks inside the root:
   - [data-spellbound-reveal]
       [data-spellbound-slider] or .sc-reveal-input
       [data-spellbound-frame] or .sc-reveal
       [data-spellbound-label="day"|"night"] or .sc-reveal-label-*
   - [data-spellbound-lookbook]
       [data-spellbound-track] or .sc-lookbook-carousel
       [data-spellbound-photo] or .sc-lookbook-photo
         [data-spellbound-video] frames hold a <video> and a
         [data-spellbound-playback] button (muted in-view autoplay)
       [data-spellbound-prev] / [data-spellbound-next] or .sc-lookbook-prev/next
       [data-spellbound-counter] (optional "01 / 03" position readout)
   - [data-spellbound-glow]
       [data-spellbound-synced] or [data-synced-product]
         data-day-image, data-glow-image, a poster img, and a canvas
       [data-spellbound-toggle] or [data-glow-toggle]
   - [data-spellbound-lock]
       .product__form.hidden! wrappers that follow main-product's lock:
       they are revealed when a .product__form outside the root loses
       hidden! (the inline lock script only sees elements rendered before it)

   Assigned ids: {sectionId}-reveal-slider, {sectionId}-lookbook,
   {sectionId}-glow-{n}. sectionId is data-section-id, otherwise the
   shopify-section wrapper id without its prefix.
*/
(() => {
  if (window.SpellboundCollection) {
    window.SpellboundCollection.mount(document);
    return;
  }

  const ROOTS =
    '[data-spellbound-reveal], [data-spellbound-lookbook], [data-spellbound-glow], [data-spellbound-lock]';
  const LOCKED_CLASS = 'hidden!';
  const VIDEO_VIEW_THRESHOLD = 0.2;
  const FRAME_ENDS = [
    2000, 2080, 2160, 2240, 2320, 2400, 2480, 2560, 2640, 2720, 2800, 2880,
    4880, 4960, 5040, 5120, 5200, 5280, 5360, 5440, 5520, 5600, 5680, 5760,
  ];
  const LOOP_MS = FRAME_ENDS[FRAME_ENDS.length - 1];
  const PHOTO = '[data-spellbound-photo], .sc-lookbook-photo';
  const TRACK = '[data-spellbound-track], .sc-lookbook-carousel';

  const instances = new WeakMap();
  const sectionNodes = new WeakMap();
  const keyByRoot = new WeakMap();
  const assignedKeys = new Map();
  const mounted = new Set();

  function on(target, type, handler, options) {
    if (typeof target.addEventListener === 'function') {
      target.addEventListener(type, handler, options);
      return function remove() {
        target.removeEventListener(type, handler, options);
      };
    }
    if (typeof target.addListener === 'function') {
      target.addListener(handler);
      return function remove() {
        target.removeListener(handler);
      };
    }
    return function remove() {};
  }

  function pick(root, selector) {
    if (root.nodeType === 1 && root.matches(selector)) return root;
    return root.querySelector(selector);
  }

  function collect(scope) {
    const found = [];
    if (!scope) return found;
    if (scope.nodeType === 1 && scope.matches(ROOTS)) found.push(scope);
    if (typeof scope.querySelectorAll === 'function') {
      scope.querySelectorAll(ROOTS).forEach((node) => {
        if (found.indexOf(node) === -1) found.push(node);
      });
    }
    return found;
  }

  function sectionKey(root) {
    const cached = keyByRoot.get(root);
    if (cached) return cached;
    const wrapper =
      typeof root.closest === 'function'
        ? root.closest('[id^="shopify-section-"]')
        : null;
    const raw =
      root.getAttribute('data-section-id') ||
      (wrapper && wrapper.id.replace(/^shopify-section-/, '')) ||
      (root.id && root.id.replace(/^shopify-section-/, '')) ||
      'spellbound';
    const base =
      String(raw)
        .trim()
        .replace(/[^A-Za-z0-9_-]+/g, '-') || 'spellbound';
    let key = base;
    let suffix = 2;
    while (assignedKeys.has(key) && assignedKeys.get(key) !== root) {
      key = `${base}-${suffix}`;
      suffix += 1;
    }
    assignedKeys.set(key, root);
    keyByRoot.set(root, key);
    return key;
  }

  function releaseKey(root) {
    const key = keyByRoot.get(root);
    if (key && assignedKeys.get(key) === root) assignedKeys.delete(key);
    keyByRoot.delete(root);
  }

  function isSafeImageUrl(url) {
    if (typeof url !== 'string') return false;
    const value = url.trim();
    if (!value) return false;
    let parsed;
    try {
      parsed = new URL(value, window.location.href);
    } catch (error) {
      return false;
    }
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:')
      return true;
    if (parsed.protocol === 'blob:') return true;
    if (parsed.protocol === 'data:') return /^data:image\//i.test(value);
    return false;
  }

  function attrUrl(element, name) {
    const value = element.getAttribute(name);
    return value ? value.trim() : '';
  }

  function loadDecoded(url) {
    if (!isSafeImageUrl(url)) {
      return Promise.reject(new Error('Unsupported image URL'));
    }
    const image = new Image();
    const absolute = new URL(url.trim(), window.location.href);
    if (
      (absolute.protocol === 'http:' || absolute.protocol === 'https:') &&
      absolute.origin !== window.location.origin
    ) {
      image.crossOrigin = 'anonymous';
    }
    const loaded = new Promise((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image failed'));
      image.src = absolute.href;
      if (image.complete) {
        if (image.naturalWidth) resolve();
        else reject(new Error('Image failed'));
      }
    });
    return loaded.then(() => {
      const decoded =
        typeof image.decode === 'function' ? image.decode() : Promise.resolve();
      return decoded.then(() => {
        if (!image.naturalWidth || !image.naturalHeight) {
          throw new Error('Empty image');
        }
        return image;
      });
    });
  }

  function mountReveal(root) {
    const slider = pick(root, '[data-spellbound-slider], .sc-reveal-input');
    const frame = pick(root, '[data-spellbound-frame], .sc-reveal');
    if (!slider || !frame) return null;
    const dayLabel = root.querySelector(
      '[data-spellbound-label="day"], .sc-reveal-label-day',
    );
    const nightLabel = root.querySelector(
      '[data-spellbound-label="night"], .sc-reveal-label-night',
    );
    slider.id = `${sectionKey(root)}-reveal-slider`;

    function update() {
      const value = Number(slider.value);
      frame.style.setProperty('--daylight', `${value}%`);
      slider.setAttribute(
        'aria-valuetext',
        `${value}% daylight, ${100 - value}% after dark`,
      );
      if (dayLabel) dayLabel.hidden = value === 0;
      if (nightLabel) nightLabel.hidden = value === 100;
    }

    const removeInput = on(slider, 'input', update);
    update();
    return function destroyReveal() {
      removeInput();
    };
  }

  function pad2(value) {
    return String(value).padStart(2, '0');
  }

  /* One lookbook <video>: muted loop that plays while at least 20% of the
     frame is in view, pauses off screen or in a hidden tab, and keeps a
     user's pause until they press play again. Native controls are the
     fallback for reduced motion or when the browser rejects autoplay. */
  function mountLookbookVideo(frame, motion) {
    const video = frame.querySelector('video');
    if (!video) return null;
    const button = frame.querySelector('[data-spellbound-playback]');
    let alive = true;
    let visible = !('IntersectionObserver' in window);
    let userPaused = false;
    let automaticPause = false;
    let started = false;
    let pending = null;
    let native = true;
    const removals = [];

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;

    function sync() {
      if (!alive || !button) return;
      const playing = !video.paused && !video.ended;
      const label = playing ? 'Pause lookbook video' : 'Play lookbook video';
      button.dataset.playing = String(playing);
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }

    function useNativeControls() {
      native = true;
      video.controls = true;
      frame.classList.remove('has-custom-controls');
      if (button) button.hidden = true;
      sync();
    }

    function useCustomControls() {
      if (!button || motion.matches) {
        useNativeControls();
        return;
      }
      native = false;
      video.controls = false;
      frame.classList.add('has-custom-controls');
      button.hidden = false;
      sync();
    }

    function pause() {
      if (video.paused) return;
      automaticPause = true;
      video.pause();
    }

    function play(fromUser) {
      if (!alive || pending || !video.paused) return;
      video.muted = true;
      let attempt;
      try {
        attempt = video.play();
      } catch (error) {
        attempt = Promise.reject(error);
      }
      if (!attempt || typeof attempt.then !== 'function') {
        sync();
        return;
      }
      pending = attempt;
      attempt
        .then(() => {
          if (!alive) return;
          if (userPaused || (!fromUser && !visible)) pause();
        })
        .catch((error) => {
          if (!alive) return;
          // AbortError means we paused mid-start; anything else is a
          // refused autoplay, so hand playback to the native controls.
          if (error && error.name === 'AbortError') return;
          useNativeControls();
        })
        .then(() => {
          pending = null;
          sync();
        });
    }

    function update() {
      if (!alive) return;
      if (!visible || document.hidden || motion.matches) {
        pause();
        return;
      }
      if (userPaused) return;
      // Custom controls autoplay; native controls only resume a video the
      // visitor already started themselves.
      if (!native || started) play(false);
    }

    function onPause() {
      if (!automaticPause) userPaused = true;
      automaticPause = false;
      sync();
    }

    function onPlay() {
      userPaused = false;
      started = true;
      sync();
    }

    function onToggle() {
      if (!video.paused && !video.ended) {
        userPaused = true;
        video.pause();
      } else {
        userPaused = false;
        play(true);
      }
      sync();
    }

    function onMotion() {
      if (motion.matches) {
        pause();
        useNativeControls();
      } else {
        useCustomControls();
        update();
      }
    }

    removals.push(on(video, 'play', onPlay));
    removals.push(on(video, 'pause', onPause));
    removals.push(on(video, 'ended', sync));
    removals.push(on(video, 'loadeddata', update));
    removals.push(on(document, 'visibilitychange', update));
    removals.push(on(motion, 'change', onMotion));
    if (button) removals.push(on(button, 'click', onToggle));

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (!alive) return;
          const entry = entries[entries.length - 1];
          visible =
            entry.isIntersecting &&
            entry.intersectionRatio >= VIDEO_VIEW_THRESHOLD - 0.001;
          update();
        },
        { threshold: [0, VIDEO_VIEW_THRESHOLD] },
      );
      observer.observe(frame);
    }

    useCustomControls();
    update();

    return function destroyLookbookVideo() {
      alive = false;
      if (observer) observer.disconnect();
      removals.forEach((remove) => remove());
      pause();
    };
  }

  function mountLookbook(root) {
    const track = pick(root, TRACK);
    if (!track) return null;
    const previous = root.querySelector(
      '[data-spellbound-prev], .sc-lookbook-prev',
    );
    const next = root.querySelector(
      '[data-spellbound-next], .sc-lookbook-next',
    );
    const counter = root.querySelector('[data-spellbound-counter]');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    track.id = `${sectionKey(root)}-lookbook`;
    if (previous) previous.setAttribute('aria-controls', track.id);
    if (next) next.setAttribute('aria-controls', track.id);
    if (counter) counter.setAttribute('for', track.id);
    if (!track.hasAttribute('tabindex')) track.setAttribute('tabindex', '0');

    function items() {
      return Array.from(track.querySelectorAll(PHOTO));
    }

    function step() {
      const photos = items();
      if (photos.length > 1) {
        const delta = photos[1].offsetLeft - photos[0].offsetLeft;
        if (delta > 0) return delta;
      }
      return track.clientWidth || 1;
    }

    function move(left) {
      scrollTrack(track, left, motion.matches ? 'instant' : 'smooth');
    }

    function activeIndex(count, max) {
      if (count < 2) return 0;
      if (max > 2 && track.scrollLeft >= max - 2) return count - 1;
      const index = Math.round(track.scrollLeft / step());
      return Math.max(0, Math.min(count - 1, index));
    }

    function updateArrows() {
      const max = track.scrollWidth - track.clientWidth;
      if (previous) previous.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = max - track.scrollLeft <= 2;
      if (counter) {
        const count = items().length;
        const text = `${pad2(activeIndex(count, max) + 1)} / ${pad2(count)}`;
        if (counter.textContent !== text) counter.textContent = text;
      }
    }

    let drag = null;
    const removals = [];

    if (previous) {
      removals.push(
        on(previous, 'click', () => move(track.scrollLeft - step())),
      );
    }
    if (next) {
      removals.push(on(next, 'click', () => move(track.scrollLeft + step())));
    }
    removals.push(on(track, 'scroll', updateArrows, { passive: true }));

    function onPointerDown(event) {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      // Leave clicks on the video controls alone; pointer capture would
      // otherwise swallow them as a drag.
      const target = event.target;
      if (
        target &&
        typeof target.closest === 'function' &&
        target.closest('[data-spellbound-playback], video[controls]')
      ) {
        return;
      }
      drag = { id: event.pointerId, x: event.clientX, left: track.scrollLeft };
      track.classList.add('is-dragging');
      try {
        track.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer is already gone; move events still scroll while it is over the track.
      }
    }

    function onPointerMove(event) {
      if (!drag || event.pointerId !== drag.id) return;
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = drag.left + drag.x - event.clientX;
    }

    function releasePointer(pointerId) {
      if (!track.hasPointerCapture || !track.hasPointerCapture(pointerId))
        return;
      try {
        track.releasePointerCapture(pointerId);
      } catch (error) {
        // Already released.
      }
    }

    function finish(event) {
      if (!drag || event.pointerId !== drag.id) return;
      const left = track.scrollLeft;
      const pointerId = drag.id;
      drag = null;
      releasePointer(pointerId);
      track.classList.remove('is-dragging');
      const size = step();
      move(Math.round(left / size) * size);
    }

    function onKeyDown(event) {
      if (
        event.target !== track ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey
      ) {
        return;
      }
      let left;
      if (event.key === 'ArrowRight') left = track.scrollLeft + step();
      else if (event.key === 'ArrowLeft') left = track.scrollLeft - step();
      else if (event.key === 'Home') left = 0;
      else if (event.key === 'End')
        left = track.scrollWidth - track.clientWidth;
      else return;
      event.preventDefault();
      move(left);
    }

    removals.push(on(track, 'pointerdown', onPointerDown));
    removals.push(on(track, 'pointermove', onPointerMove, { passive: false }));
    removals.push(on(track, 'pointerup', finish));
    removals.push(on(track, 'pointercancel', finish));
    removals.push(on(track, 'lostpointercapture', finish));
    removals.push(on(track, 'keydown', onKeyDown));

    let observer = null;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(updateArrows);
      observer.observe(track);
    } else {
      removals.push(on(window, 'resize', updateArrows));
    }
    updateArrows();

    const videoCleanups = Array.from(
      track.querySelectorAll('[data-spellbound-video]'),
    )
      .map((frame) => mountLookbookVideo(frame, motion))
      .filter(Boolean);

    return function destroyLookbook() {
      const pointerId = drag && drag.id;
      drag = null;
      track.classList.remove('is-dragging');
      videoCleanups.forEach((cleanup) => cleanup());
      removals.forEach((remove) => remove());
      if (observer) observer.disconnect();
      if (pointerId != null) releasePointer(pointerId);
    };
  }

  /* Buy controls that follow main-product's lock. The inline lock script
     collects `.product__form` when it is parsed, before this section exists,
     so we watch a locked wrapper it does know about and reveal ours when
     that one loses `hidden!`. With no locked wrapper elsewhere on the page
     there is nothing to wait for and the controls show straight away. */
  function mountLock(root) {
    const wrappers = Array.from(
      root.querySelectorAll('[data-spellbound-locked], .product__form'),
    );
    if (!wrappers.length) return null;

    function reveal() {
      wrappers.forEach((wrapper) => wrapper.classList.remove(LOCKED_CLASS));
    }

    const references = Array.from(
      document.querySelectorAll('.product__form'),
    ).filter(
      (element) =>
        !root.contains(element) && element.classList.contains(LOCKED_CLASS),
    );
    if (!references.length) {
      reveal();
      return null;
    }

    function unlocked() {
      return references.some(
        (element) => !element.classList.contains(LOCKED_CLASS),
      );
    }

    if (!('MutationObserver' in window)) {
      reveal();
      return null;
    }

    let observer = new MutationObserver(() => {
      if (!observer || !unlocked()) return;
      reveal();
      observer.disconnect();
      observer = null;
    });
    references.forEach((element) => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class'],
      });
    });

    return function destroyLock() {
      if (observer) observer.disconnect();
      observer = null;
    };
  }

  function inView(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return true;
    return (
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  }

  function mountGlow(root) {
    const containers = Array.from(
      root.querySelectorAll('[data-spellbound-synced], [data-synced-product]'),
    ).filter((container) => attrUrl(container, 'data-glow-image'));
    if (!containers.length) return null;

    const toggles = Array.from(
      root.querySelectorAll('[data-spellbound-toggle], [data-glow-toggle]'),
    );
    const key = sectionKey(root);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let alive = true;
    let paused = reducedMotion.matches;
    let visible = inView(root);
    let ready = false;
    let requestId = null;
    let elapsed = 0;
    let startedAt = 0;
    let lastFrame = -1;
    let players = [];
    const removals = [];

    containers.forEach((container, index) => {
      const canvas = container.querySelector('canvas');
      if (!canvas) return;
      canvas.id = `${key}-glow-${index}`;
      canvas.hidden = true;
    });
    const controls = containers
      .map((container) => container.querySelector('canvas'))
      .filter(Boolean)
      .map((canvas) => canvas.id)
      .join(' ');
    toggles.forEach((toggle) => {
      toggle.hidden = true;
      if (controls) toggle.setAttribute('aria-controls', controls);
    });

    function conceal() {
      toggles.forEach((toggle) => {
        toggle.hidden = true;
      });
      containers.forEach((container) => {
        const canvas = container.querySelector('canvas');
        if (canvas) canvas.hidden = true;
      });
    }

    function draw(frame) {
      if (!alive || frame === lastFrame || !players.length) return;
      const glow = frame <= 12 ? frame / 12 : (24 - frame) / 12;
      // Every card is drawn in this callback, before the browser paints.
      for (let index = 0; index < players.length; index += 1) {
        const player = players[index];
        const width = player.canvas.width;
        const height = player.canvas.height;
        player.context.globalAlpha = 1;
        player.context.drawImage(player.daylight, 0, 0, width, height);
        player.context.globalAlpha = glow;
        player.context.drawImage(player.image, 0, 0, width, height);
        player.context.globalAlpha = 1;
        player.canvas.dataset.frame = String(frame);
        const dark = String(glow >= 2 / 3);
        if (player.container.dataset.glowDark !== dark) {
          player.container.dataset.glowDark = dark;
        }
      }
      lastFrame = frame;
    }

    function stop() {
      if (requestId === null) return;
      window.cancelAnimationFrame(requestId);
      requestId = null;
      elapsed = (elapsed + performance.now() - startedAt) % LOOP_MS;
    }

    function tick(now) {
      if (!alive) return;
      try {
        const phase = (elapsed + now - startedAt) % LOOP_MS;
        const frame = FRAME_ENDS.findIndex((end) => phase < end);
        if (frame >= 0) draw(frame);
      } catch (error) {
        fail();
        return;
      }
      if (!alive) return;
      requestId = window.requestAnimationFrame(tick);
    }

    function syncPlayback() {
      if (!alive) return;
      const label = paused ? 'Play glow animations' : 'Stop glow animations';
      toggles.forEach((toggle) => {
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('title', label);
        toggle.dataset.playing = String(!paused);
      });
      if (!ready || paused || !visible || document.hidden) {
        stop();
        return;
      }
      if (requestId !== null) return;
      startedAt = performance.now();
      requestId = window.requestAnimationFrame(tick);
    }

    function fail() {
      if (!alive) return;
      ready = false;
      stop();
      conceal();
    }

    function onToggle() {
      paused = !paused;
      syncPlayback();
    }

    function onMotion(event) {
      stop();
      paused = event.matches;
      if (paused && ready) {
        elapsed = 0;
        lastFrame = -1;
        try {
          draw(0);
        } catch (error) {
          fail();
          return;
        }
      }
      syncPlayback();
    }

    toggles.forEach((toggle) => {
      removals.push(on(toggle, 'click', onToggle));
    });
    removals.push(on(document, 'visibilitychange', syncPlayback));
    removals.push(on(reducedMotion, 'change', onMotion));

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver((entries) => {
        if (!alive) return;
        visible = entries.some((entry) => entry.isIntersecting);
        syncPlayback();
      });
      observer.observe(root);
    }

    Promise.all(
      containers.map(async (container) => {
        const canvas = container.querySelector('canvas');
        const context = canvas && canvas.getContext('2d', { alpha: false });
        if (!context || !canvas.width || !canvas.height) {
          throw new Error('Canvas unavailable');
        }
        const poster = container.querySelector('img');
        const dayUrl =
          attrUrl(container, 'data-day-image') ||
          (poster && (poster.currentSrc || poster.getAttribute('src'))) ||
          '';
        const glowUrl = attrUrl(container, 'data-glow-image');
        const images = await Promise.all([
          loadDecoded(dayUrl),
          loadDecoded(glowUrl),
        ]);
        if (!alive) return null;
        return {
          canvas,
          context,
          daylight: images[0],
          image: images[1],
          container,
        };
      }),
    )
      .then((loaded) => {
        if (!alive || loaded.some((player) => !player)) return;
        players = loaded;
        try {
          draw(0);
        } catch (error) {
          fail();
          return;
        }
        ready = true;
        syncPlayback();
        players.forEach((player) => {
          player.canvas.hidden = false;
        });
        toggles.forEach((toggle) => {
          toggle.hidden = false;
        });
      })
      .catch(() => {
        fail();
      });

    return function destroyGlow() {
      alive = false;
      ready = false;
      stop();
      if (observer) observer.disconnect();
      removals.forEach((remove) => remove());
      players = [];
    };
  }

  function destroyOne(root) {
    const cleanups = instances.get(root);
    if (!cleanups) return;
    instances.delete(root);
    mounted.delete(root);
    sectionNodes.delete(root);
    releaseKey(root);
    cleanups.forEach((cleanup) => cleanup());
  }

  function mountOne(root) {
    if (instances.has(root)) return;
    const cleanups = [];
    if (root.hasAttribute('data-spellbound-reveal')) {
      const cleanup = mountReveal(root);
      if (cleanup) cleanups.push(cleanup);
    }
    if (root.hasAttribute('data-spellbound-lookbook')) {
      const cleanup = mountLookbook(root);
      if (cleanup) cleanups.push(cleanup);
    }
    if (root.hasAttribute('data-spellbound-glow')) {
      const cleanup = mountGlow(root);
      if (cleanup) cleanups.push(cleanup);
    }
    if (root.hasAttribute('data-spellbound-lock')) {
      const cleanup = mountLock(root);
      if (cleanup) cleanups.push(cleanup);
    }
    if (!cleanups.length) return;
    instances.set(root, cleanups);
    mounted.add(root);
    const wrapper =
      (typeof root.closest === 'function' &&
        root.closest('[id^="shopify-section-"]')) ||
      root;
    sectionNodes.set(root, wrapper);
  }

  function mount(scope) {
    collect(scope || document).forEach(mountOne);
  }

  function destroy(scope) {
    if (
      !scope ||
      scope === document ||
      scope === document.documentElement ||
      scope === document.body
    ) {
      Array.from(mounted).forEach(destroyOne);
      return;
    }
    const targets = new Set(collect(scope));
    mounted.forEach((root) => {
      const wrapper = sectionNodes.get(root);
      if (
        root === scope ||
        wrapper === scope ||
        (typeof scope.contains === 'function' && scope.contains(root))
      ) {
        targets.add(root);
      }
    });
    targets.forEach(destroyOne);
  }

  function photoFromBlock(target) {
    if (!target || typeof target.closest !== 'function') return null;
    const root = target.closest('[data-spellbound-lookbook]');
    if (!root) return null;
    if (typeof target.matches === 'function' && target.matches(PHOTO)) {
      return root.contains(target) ? target : null;
    }
    if (typeof target.querySelector === 'function') {
      const nested = target.querySelector(PHOTO);
      if (nested && root.contains(nested)) return nested;
    }
    const parent = target.closest(PHOTO);
    if (parent && root.contains(parent)) return parent;
    return null;
  }

  function scrollTrack(track, left, behavior) {
    try {
      track.scrollTo({ left, behavior });
    } catch (error) {
      track.scrollLeft = left;
    }
  }

  function alignLookbook(photo) {
    const root = photo.closest('[data-spellbound-lookbook]');
    if (!root) return;
    const track = pick(root, TRACK);
    if (!track || !track.contains(photo)) return;
    const photos = Array.from(track.querySelectorAll(PHOTO));
    const origin = photos.length ? photos[0].offsetLeft : 0;
    scrollTrack(track, Math.max(0, photo.offsetLeft - origin), 'instant');
  }

  let blockScrollTimer = 0;

  function onBlockSelect(event) {
    const photo = photoFromBlock(event.target);
    if (!photo) return;
    const run = () => {
      if (photo.isConnected) alignLookbook(photo);
    };
    run();
    window.requestAnimationFrame(run);
    window.clearTimeout(blockScrollTimer);
    blockScrollTimer = window.setTimeout(run, 200);
  }

  function onSectionLoad(event) {
    mount(event.target);
  }

  function onSectionUnload(event) {
    destroy(event.target);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:block:select', onBlockSelect);

  window.SpellboundCollection = {
    mount,
    destroy,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(document), {
      once: true,
    });
  } else {
    mount(document);
  }
})();
